import { useObserver } from 'mobx-react-lite';
import _ from 'lodash';
import { useGetCollectionsNavigationQuery } from 'types/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getCategoriesForMenuForState } from 'shared/helpers/products';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'hooks/use-cart';
import { menuFilters } from 'hooks/use-menu-filter-options';
import { useQuery } from 'hooks/use-query';
import { useUserSegments } from 'hooks/use-user-segments';

/**
 * @typedef {Object} Category
 * @property {string} key
 * @property {string} label
 * @property {string} value
 * @property {string} [slug]
 * @property {boolean} [customCategory]
 */

/**
 * @typedef {Object} UseDispensaryCategoriesReturn
 * @property {Category[]} categories
 */

/**
 * @returns {UseDispensaryCategoriesReturn}
 */
export default function useDispensaryCategories() {
  const { dispensary } = useDispensary();
  const cart = useCart();
  const menuType = useObserver(() => cart.menuType);
  const { isKiosk } = useUI();
  const flags = useFlags();

  let categories = getCategoriesForMenuForState(dispensary?.location?.state);
  let loadedCategories = dispensary?.activeCategories || [];
  const categoriesV2 = dispensary?.webCustomizationSettingsV2?.categories?.items;
  const isEnabledCustomCategories = flags['growth.ecomm.custom-categories.rollout'] ?? false;
  const isExclusiveCollectionsEnabled = flags['ecommerce.exclusive-collections.rollout'] ?? false;

  /**
   * SEGMENTS IN VARIABLES FOR CACHE DIFFERENTIATION:
   *
   * User segments are fetched and included in query variables, enabling Apollo and CDN
   * to cache separately for each segment combination. This allows cache-first fetchPolicy
   * and PUBLIC CDN caching while maintaining correct per-user results.
   *
   * Different segment values in variables create different cache keys, so users with
   * different segments automatically get different cached responses.
   *
   * Server validates client-provided segments to prevent spoofing.
   */
  const { segments: userSegments } = useUserSegments({
    dispensaryId: dispensary?.id,
  });

  const { data } = useQuery(menuFilters, {
    skip: !dispensary,
    fetchPolicy: isExclusiveCollectionsEnabled ? 'cache-first' : undefined,
    variables: {
      dispensaryId: dispensary?.id,
      pricingType: menuType,
      isKioskMenu: isKiosk,
      userSegmentIds: isExclusiveCollectionsEnabled ? userSegments : undefined,
    },
  });

  const { data: collectionsData } = useGetCollectionsNavigationQuery({
    skip: !isEnabledCustomCategories || !dispensary?.id,
    variables: {
      dispensaryId: dispensary?.id,
      customCategory: true,
      enabled: true,
    },
  });

  const productCategories = _.get(data, `menuFiltersV2.categoriesSubcategories`, []);

  if (productCategories.length) {
    loadedCategories = productCategories.map(({ category }) => category);
  }

  if (loadedCategories.length) {
    categories = _.filter(categories, (category) => _.includes(loadedCategories, category.value));
  }

  if (categoriesV2) {
    const orderMap = new Map(categoriesV2.map((item, index) => [item.category, index]));

    if (collectionsData?.getCollectionsNavigation) {
      categories = [
        ...categories,
        ...collectionsData?.getCollectionsNavigation.map((collection) => ({
          customCategory: collection.enabled,
          slug: collection.slug,
          key: collection.id,
          label: collection.title,
          value: collection.title,
        })),
      ];
    }

    categories.sort((a, b) => (orderMap.get(a.value) ?? 0) - (orderMap.get(b.value) ?? 0));
  }

  return { categories };
}
